import React, { useState, useEffect } from 'react';
import {
  AssetRecord,
  AssetTypeRecord,
  AssetDashboardKpis,
  AssetInspectionRecord,
  AssetMaintenanceEvent,
  CalibrationEvent,
  AssetLocationHistory,
} from './types';
import { assetsService } from './assetsService';
import { AssetsMetricsCards } from './components/AssetsMetricsCards';
import { AssetCreateModal } from './components/AssetCreateModal';
import { AssetInspectionModal } from './components/AssetInspectionModal';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const AssetsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'inventory' | 'fleet' | 'inspections' | 'maintenance' | 'calibrations'>('inventory');
  const [kpis, setKpis] = useState<AssetDashboardKpis | null>(null);
  const [types, setTypes] = useState<AssetTypeRecord[]>([]);
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [inspections, setInspections] = useState<AssetInspectionRecord[]>([]);
  const [maintenanceEvents, setMaintenanceEvents] = useState<AssetMaintenanceEvent[]>([]);
  const [calibrations, setCalibrations] = useState<CalibrationEvent[]>([]);

  // Selected Asset for details modal / drawer
  const [selectedAsset, setSelectedAsset] = useState<AssetRecord | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<AssetLocationHistory[]>([]);

  // Modals
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isInspectionModalOpen, setIsInspectionModalOpen] = useState(false);

  // Filters
  const [filterDomain, setFilterDomain] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [kpiData, typeData, assetData, inspData, maintData, calData] = await Promise.all([
        assetsService.getDashboardKpis(),
        assetsService.getAssetTypes(),
        assetsService.getAssets(),
        assetsService.getInspections(),
        assetsService.getMaintenanceEvents(),
        assetsService.getCalibrations(),
      ]);
      setKpis(kpiData);
      setTypes(typeData);
      setAssets(assetData);
      setInspections(inspData);
      setMaintenanceEvents(maintData);
      setCalibrations(calData);
    } catch (err) {
      console.error('Error loading asset data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAsset = async (data: any) => {
    await assetsService.createAsset(data);
    await loadAll();
  };

  const handleScheduleInspection = async (data: any) => {
    await assetsService.scheduleInspection(data);
    await loadAll();
  };

  const handleSelectAsset = async (asset: AssetRecord) => {
    setSelectedAsset(asset);
    try {
      const locs = await assetsService.getAssetLocations(asset.id);
      setSelectedLocations(locs);
    } catch (err) {
      console.error('Failed to load asset locations:', err);
    }
  };

  const filteredAssets = assets.filter((a) => {
    if (activeTab === 'fleet' && a.asset_domain !== 'AIRCRAFT') return false;
    if (filterDomain && a.asset_domain !== filterDomain) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const match =
        a.asset_number.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        (a.serial_number && a.serial_number.toLowerCase().includes(q)) ||
        (a.aircraft_details?.registration_mark && a.aircraft_details.registration_mark.toLowerCase().includes(q));
      if (!match) return false;
    }
    return true;
  });

  const { sortedItems: sortedAssets, sortField: assetSortField, sortDirection: assetSortDir, toggleSort: toggleAssetSort } = useTableSort(filteredAssets);
  const { sortedItems: sortedInspections, sortField: inspSortField, sortDirection: inspSortDir, toggleSort: toggleInspSort } = useTableSort(inspections);
  const { sortedItems: sortedMaintenance, sortField: maintSortField, sortDirection: maintSortDir, toggleSort: toggleMaintSort } = useTableSort(maintenanceEvents);
  const { sortedItems: sortedCalibrations, sortField: calSortField, sortDirection: calSortDir, toggleSort: toggleCalSort } = useTableSort(calibrations);

  const {
    currentPage: assetPage,
    setCurrentPage: setAssetPage,
    pageSize: assetPageSize,
    setPageSize: setAssetPageSize,
    totalItems: totalAssetItems,
    paginatedItems: paginatedAssets,
  } = useTablePagination(sortedAssets, 25);

  return (
    <div style={{ maxWidth: '1440px', margin: '0 auto', padding: '0 24px' }}>
      {/* Top Banner */}
      <div className="page-header-banner">
        <div className="badge">FLEET & CAMO OPERATIONS</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Aviation Assets & Fleet Management</h1>
            <p>Aircraft CAMO tracking, Ground Support Equipment (GSE), calibrated tools, and Part-145 facility inventory.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => setIsInspectionModalOpen(true)}
              className="btn-secondary"
              style={{ width: 'auto' }}
            >
              📋 Schedule Inspection
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="btn-primary"
              style={{ width: 'auto', padding: '10px 18px' }}
            >
              + Add New Asset
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <AssetsMetricsCards kpis={kpis} />

      {/* Navigation Sub-Tabs */}
      <div className="nav-tabs">
        {[
          { key: 'inventory', label: 'Asset Inventory' },
          { key: 'fleet', label: '✈️ Aircraft Fleet & CAMO' },
          { key: 'inspections', label: '🔍 Inspections' },
          { key: 'maintenance', label: '🛠️ Maintenance Events (Part-145)' },
          { key: 'calibrations', label: '📐 Tool Calibrations (ISO 17025)' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              setActiveTab(tab.key as any);
              if (tab.key === 'fleet') setFilterDomain('');
            }}
            className={`tab-btn ${activeTab === tab.key ? 'active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Main Tab Content */}
      {(activeTab === 'inventory' || activeTab === 'fleet') && (
        <div>
          {/* Filters Bar */}
          <div
            style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              background: '#ffffff',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
            }}
          >
            <input
              type="text"
              placeholder="Search by asset no, registration, name, or serial no..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                padding: '7px 12px',
                borderRadius: '6px',
                border: '1px solid #cbd5e1',
                fontSize: '13px',
              }}
            />
            {activeTab === 'inventory' && (
              <select
                value={filterDomain}
                onChange={(e) => setFilterDomain(e.target.value)}
                style={{
                  padding: '7px 12px',
                  borderRadius: '6px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13px',
                }}
              >
                <option value="">All Domains</option>
                <option value="AIRCRAFT">AIRCRAFT (Fleet)</option>
                <option value="EQUIPMENT">EQUIPMENT (Ground Support)</option>
                <option value="TOOL">TOOL (Calibrated Tooling)</option>
                <option value="FACILITY">FACILITY (Hangars & Buildings)</option>
                <option value="VEHICLE">VEHICLE (Apron Vehicles)</option>
              </select>
            )}
          </div>

          {/* Asset List Table */}
          <div className="table-container" style={{ margin: 0, border: 'none', borderRadius: 0 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 10 }}>
                <tr>
                  <SortableTh field="asset_number" currentField={assetSortField} currentDirection={assetSortDir} onSort={toggleAssetSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                    Asset No / Reg
                  </SortableTh>
                  <SortableTh field="name" currentField={assetSortField} currentDirection={assetSortDir} onSort={toggleAssetSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                    Name & Model
                  </SortableTh>
                  <SortableTh field="asset_domain" currentField={assetSortField} currentDirection={assetSortDir} onSort={toggleAssetSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                    Domain / Type
                  </SortableTh>
                  <SortableTh field="current_location" currentField={assetSortField} currentDirection={assetSortDir} onSort={toggleAssetSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                    Current Location
                  </SortableTh>
                  <SortableTh field="criticality" currentField={assetSortField} currentDirection={assetSortDir} onSort={toggleAssetSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                    Criticality
                  </SortableTh>
                  <SortableTh field="status" currentField={assetSortField} currentDirection={assetSortDir} onSort={toggleAssetSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                    Status
                  </SortableTh>
                  <th style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && filteredAssets.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                      No asset records found matching criteria.
                    </td>
                  </tr>
                ) : (
                  paginatedAssets.map((asset) => (
                    <tr
                      key={asset.id}
                      style={{
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        transition: 'background 0.15s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      onClick={() => handleSelectAsset(asset)}
                    >
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>
                        <div>{asset.asset_number}</div>
                        {asset.aircraft_details && (
                          <div style={{ fontSize: '11px', color: '#0284c7' }}>
                            ✈️ {asset.aircraft_details.registration_mark} (MSN: {asset.aircraft_details.msn})
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#334155' }}>
                        <div style={{ fontWeight: 500 }}>{asset.name}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          {asset.manufacturer} {asset.model} {asset.serial_number ? `(S/N: ${asset.serial_number})` : ''}
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background:
                              asset.asset_domain === 'AIRCRAFT'
                                ? '#e0f2fe'
                                : asset.asset_domain === 'TOOL'
                                ? '#f3e8ff'
                                : '#f1f5f9',
                            color:
                              asset.asset_domain === 'AIRCRAFT'
                                ? '#0369a1'
                                : asset.asset_domain === 'TOOL'
                                ? '#6b21a8'
                                : '#475569',
                          }}
                        >
                          {asset.asset_domain}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#475569' }}>
                        📍 {asset.current_location || 'Not specified'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background:
                              asset.criticality === 'CRITICAL'
                                ? '#fee2e2'
                                : asset.criticality === 'HIGH'
                                ? '#ffedd5'
                                : '#f1f5f9',
                            color:
                              asset.criticality === 'CRITICAL'
                                ? '#b91c1c'
                                : asset.criticality === 'HIGH'
                                ? '#c2410c'
                                : '#475569',
                          }}
                        >
                          {asset.criticality}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            padding: '3px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 600,
                            background:
                              asset.status === 'ACTIVE'
                                ? '#dcfce7'
                                : asset.status === 'MAINTENANCE'
                                ? '#ffedd5'
                                : asset.status === 'AOG'
                                ? '#fee2e2'
                                : '#f1f5f9',
                            color:
                              asset.status === 'ACTIVE'
                                ? '#15803d'
                                : asset.status === 'MAINTENANCE'
                                ? '#c2410c'
                                : asset.status === 'AOG'
                                ? '#b91c1c'
                                : '#475569',
                          }}
                        >
                          {asset.status}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectAsset(asset);
                          }}
                          style={{
                            background: '#f1f5f9',
                            border: '1px solid #cbd5e1',
                            padding: '4px 10px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'pointer',
                          }}
                        >
                          Details & History
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            currentPage={assetPage}
            totalItems={totalAssetItems}
            pageSize={assetPageSize}
            onPageChange={setAssetPage}
            onPageSizeChange={setAssetPageSize}
            itemLabel="assets / aircraft"
          />
        </div>
      )}

      {/* Inspections Tab */}
      {activeTab === 'inspections' && (
        <div
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <SortableTh field="asset_number" currentField={inspSortField} currentDirection={inspSortDir} onSort={toggleInspSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Asset
                </SortableTh>
                <SortableTh field="inspection_type" currentField={inspSortField} currentDirection={inspSortDir} onSort={toggleInspSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Inspection Type
                </SortableTh>
                <SortableTh field="planned_date" currentField={inspSortField} currentDirection={inspSortDir} onSort={toggleInspSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Planned Date
                </SortableTh>
                <SortableTh field="actual_date" currentField={inspSortField} currentDirection={inspSortDir} onSort={toggleInspSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Actual Date
                </SortableTh>
                <SortableTh field="inspector_name" currentField={inspSortField} currentDirection={inspSortDir} onSort={toggleInspSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Inspector
                </SortableTh>
                <SortableTh field="status" currentField={inspSortField} currentDirection={inspSortDir} onSort={toggleInspSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Result & Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedInspections.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                    No recorded inspections found.
                  </td>
                </tr>
              ) : (
                sortedInspections.map((insp) => (
                  <tr key={insp.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>
                      {insp.asset_number} — {insp.asset_name}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{ fontWeight: 500, color: '#0284c7' }}>{insp.inspection_type}</span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>{insp.planned_date}</td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>{insp.actual_date || '-'}</td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>{insp.inspector_name}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background:
                            insp.status === 'COMPLETED'
                              ? '#dcfce7'
                              : insp.status === 'OVERDUE'
                              ? '#fee2e2'
                              : '#f1f5f9',
                          color:
                            insp.status === 'COMPLETED'
                              ? '#15803d'
                              : insp.status === 'OVERDUE'
                              ? '#b91c1c'
                              : '#475569',
                        }}
                      >
                        {insp.status} {insp.result ? `(${insp.result})` : ''}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Maintenance Events Tab */}
      {activeTab === 'maintenance' && (
        <div
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <SortableTh field="work_order_number" currentField={maintSortField} currentDirection={maintSortDir} onSort={toggleMaintSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Work Order (WO)
                </SortableTh>
                <SortableTh field="asset_number" currentField={maintSortField} currentDirection={maintSortDir} onSort={toggleMaintSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Asset
                </SortableTh>
                <SortableTh field="event_type" currentField={maintSortField} currentDirection={maintSortDir} onSort={toggleMaintSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Type & Description
                </SortableTh>
                <SortableTh field="event_date" currentField={maintSortField} currentDirection={maintSortDir} onSort={toggleMaintSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Date / Flight Hours
                </SortableTh>
                <SortableTh field="performed_by" currentField={maintSortField} currentDirection={maintSortDir} onSort={toggleMaintSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Technician & CRS
                </SortableTh>
                <SortableTh field="status" currentField={maintSortField} currentDirection={maintSortDir} onSort={toggleMaintSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedMaintenance.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                    No recorded maintenance events found.
                  </td>
                </tr>
              ) : (
                sortedMaintenance.map((m) => (
                  <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>
                      {m.work_order_number}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>
                      {m.asset_number} — {m.asset_name}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 600, color: '#0284c7' }}>{m.event_type}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{m.description}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      <div>{m.event_date.slice(0, 10)}</div>
                      {m.flight_hours_at_event && (
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          ⏱️ {m.flight_hours_at_event} FH
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      <div>{m.performed_by}</div>
                      {m.maintenance_reference && (
                        <div style={{ fontSize: '11px', color: '#15803d', fontWeight: 500 }}>
                          📜 {m.maintenance_reference}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: m.status === 'COMPLETED' ? '#dcfce7' : '#ffedd5',
                          color: m.status === 'COMPLETED' ? '#15803d' : '#c2410c',
                        }}
                      >
                        {m.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Calibrations Tab */}
      {activeTab === 'calibrations' && (
        <div
          style={{
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
              <tr>
                <SortableTh field="tool_name" currentField={calSortField} currentDirection={calSortDir} onSort={toggleCalSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Tool / Instrument
                </SortableTh>
                <SortableTh field="certificate_number" currentField={calSortField} currentDirection={calSortDir} onSort={toggleCalSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Certificate & Standard
                </SortableTh>
                <SortableTh field="calibration_date" currentField={calSortField} currentDirection={calSortDir} onSort={toggleCalSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Calibration Date
                </SortableTh>
                <SortableTh field="expiry_date" currentField={calSortField} currentDirection={calSortDir} onSort={toggleCalSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Expiry Date
                </SortableTh>
                <SortableTh field="result" currentField={calSortField} currentDirection={calSortDir} onSort={toggleCalSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Result & Tolerance
                </SortableTh>
                <SortableTh field="status" currentField={calSortField} currentDirection={calSortDir} onSort={toggleCalSort} style={{ padding: '12px 16px', fontWeight: 600, color: '#475569' }}>
                  Status
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {sortedCalibrations.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>
                    No recorded tool calibrations found.
                  </td>
                </tr>
              ) : (
                sortedCalibrations.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>
                      {c.tool_number} — {c.tool_name}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 500, color: '#0284c7' }}>{c.certificate_number}</div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>{c.calibration_standard}</div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>{c.calibration_date}</td>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#0f172a' }}>{c.expiry_date}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background: c.result === 'PASS' ? '#dcfce7' : '#fee2e2',
                          color: c.result === 'PASS' ? '#15803d' : '#b91c1c',
                        }}
                      >
                        {c.result}
                      </span>
                      {c.tolerance_accuracy && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          {c.tolerance_accuracy}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                          background:
                            c.status === 'VALID'
                              ? '#dcfce7'
                              : c.status === 'EXPIRED'
                              ? '#fee2e2'
                              : '#ffedd5',
                          color:
                            c.status === 'VALID'
                              ? '#15803d'
                              : c.status === 'EXPIRED'
                              ? '#b91c1c'
                              : '#c2410c',
                        }}
                      >
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Selected Asset Details Drawer / Modal */}
      {selectedAsset && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(15, 23, 42, 0.6)',
            display: 'flex',
            justifyContent: 'flex-end',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              width: '550px',
              height: '100%',
              background: '#ffffff',
              boxShadow: '-4px 0 15px rgba(0,0,0,0.1)',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: '#f8fafc',
              }}
            >
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#0f172a', margin: 0 }}>
                  {selectedAsset.name}
                </h3>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
                  {selectedAsset.asset_number} • {selectedAsset.asset_domain}
                </p>
              </div>
              <button
                onClick={() => setSelectedAsset(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  color: '#94a3b8',
                  cursor: 'pointer',
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '24px', flex: 1 }}>
              {/* Asset Details Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Criticality</span>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedAsset.criticality}</div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Status</span>
                  <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedAsset.status}</div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Manufacturer & Model</span>
                  <div style={{ fontWeight: 500, color: '#334155' }}>
                    {selectedAsset.manufacturer || '-'} {selectedAsset.model || ''}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Serial Number</span>
                  <div style={{ fontWeight: 500, color: '#334155' }}>{selectedAsset.serial_number || '-'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Responsible Department</span>
                  <div style={{ fontWeight: 500, color: '#334155' }}>{selectedAsset.department || '-'}</div>
                </div>
                <div>
                  <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'uppercase' }}>Current Location</span>
                  <div style={{ fontWeight: 600, color: '#0284c7' }}>{selectedAsset.current_location}</div>
                </div>
              </div>

              {/* Aircraft Extension Details if applicable */}
              {selectedAsset.aircraft_details && (
                <div
                  style={{
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '24px',
                  }}
                >
                  <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0369a1', margin: '0 0 12px' }}>
                    ✈️ EASA CAMO Aircraft Details
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '12px' }}>
                    <div>
                      <span style={{ color: '#64748b' }}>Registration:</span>{' '}
                      <strong>{selectedAsset.aircraft_details.registration_mark}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>MSN:</span>{' '}
                      <strong>{selectedAsset.aircraft_details.msn}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Type Code:</span>{' '}
                      <strong>{selectedAsset.aircraft_details.icao_type_code}</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Airworthiness Status:</span>{' '}
                      <strong style={{ color: '#16a34a' }}>
                        {selectedAsset.aircraft_details.airworthiness_status}
                      </strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Total Flight Hours (FH):</span>{' '}
                      <strong>{selectedAsset.aircraft_details.total_flight_hours} hrs</strong>
                    </div>
                    <div>
                      <span style={{ color: '#64748b' }}>Total Flight Cycles (FC):</span>{' '}
                      <strong>{selectedAsset.aircraft_details.total_flight_cycles} cycles</strong>
                    </div>
                  </div>
                </div>
              )}

              {/* Movement & Location History Timeline (ASSET-017) */}
              <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', margin: '0 0 12px' }}>
                📍 Location & Movement History (Location Timeline)
              </h4>
              <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: '16px', marginLeft: '6px' }}>
                {selectedLocations.map((loc, idx) => (
                  <div key={loc.id || idx} style={{ marginBottom: '16px', position: 'relative' }}>
                    <div
                      style={{
                        position: 'absolute',
                        left: '-22px',
                        top: '2px',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        background: idx === 0 ? '#0284c7' : '#94a3b8',
                      }}
                    />
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
                      {loc.location_name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#64748b', margin: '2px 0' }}>
                      {loc.reason}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {loc.created_at.slice(0, 16).replace('T', ' ')} • Moved by: {loc.moved_by_name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {isCreateModalOpen && (
        <AssetCreateModal
          types={types}
          onClose={() => setIsCreateModalOpen(false)}
          onSubmit={handleCreateAsset}
        />
      )}

      {isInspectionModalOpen && (
        <AssetInspectionModal
          assets={assets}
          onClose={() => setIsInspectionModalOpen(false)}
          onSubmit={handleScheduleInspection}
        />
      )}
    </div>
  );
};
